<script lang="ts">
import { useFetch } from '#app/composables/fetch';
import { createError, showError } from '#app/composables/error';
import { computed, ref } from 'vue';
import type { GetPublicArticleDetailByPermalinkQueryArticleExtended } from '@shared/app/types/kiwiUserServer';
import type { RouteLocationNormalized } from 'vue-router';
import { splitStringByComma } from '@shared/app/utils/splitStringByComma';

const NEWS_DETAIL_STATE_KEY = 'public-article-news-detail';

export async function middleware(to: RouteLocationNormalized) {
  const params = to.params as { permalink: string };

  // NOTE: async関数内でawait後にcomposableを呼び出すと
  // Nuxtインスタンスのコンテキストが失われるため、事前に取得しておく
  const nuxtApp = useNuxtApp();
  const stateKey = `${NEWS_DETAIL_STATE_KEY}-${params.permalink}`;
  const state =
    useState<GetPublicArticleDetailByPermalinkQueryArticleExtended | null>(
      stateKey,
      () => null,
    );
  const { setBreadcrumbs } = useBreadcrumbs();

  try {
    const data =
      await $fetch<GetPublicArticleDetailByPermalinkQueryArticleExtended>(
        '/api/kiwi_user/articles/detail',
        {
          query: {
            folder_id: 100,
            permalink: params.permalink,
          },
        },
      );

    state.value = data ?? null;

    if (!data) {
      return;
    }

    const tags = splitStringByComma(data.tags ?? '');

    setBreadcrumbs([
      {
        to: '/public/article/news',
        text: 'ニュース',
      },
      ...(tags?.[0]
        ? [
            {
              to: {
                path: '/public/article/news',
                query: {
                  tag: tags[0],
                },
              },
              text: tags[0],
            },
          ]
        : []),
      {
        text: data.title,
      },
    ]);
  } catch (e) {
    state.value = null;
    await nuxtApp.runWithContext(() => showError(e as Error));
  }
}
</script>

<script lang="ts" setup>
import ArticleContents from '@shared/app/components/organisms/ArticleContents.vue';
import { normalizeNumberQueryParameter } from '@shared/app/utils/queryNormalizers';
import { useTransformArticle } from '@shared/app/composables/useTransformArticle';
import { useImageUrl } from '@shared/app/composables/useImageUrl';
import { definePageMeta } from '#imports';
import { useSeoMeta } from '#app/composables/head';
import { navigateTo, useRoute } from '#app/composables/router';
import { useState } from '#app/composables/state';
import { useNuxtApp } from '#app/nuxt';
import { useOgImage } from '~/composables/useOgImage';
import { useCategories } from '~/composables/useCategories';
import { useBreadcrumbs } from '~/composables/useBreadcrumbs';

const route = useRoute('public-article-news-permalink-page');

const articleContentsRef = ref<InstanceType<typeof ArticleContents>>();

definePageMeta({
  middleware,
});

const { createArticleImageUrl } = useImageUrl();
const { isPrArticle, getNewsArticleCategories } = await useCategories();

const stateKey = `${NEWS_DETAIL_STATE_KEY}-${route.params.permalink}`;
const data =
  useState<GetPublicArticleDetailByPermalinkQueryArticleExtended | null>(
    stateKey,
    () => null,
  );

const title = computed(() => {
  return data.value!.title;
});

const categories = computed(() => {
  if (!data.value) {
    return [];
  }

  return getNewsArticleCategories(data.value);
});

const imageFile = computed(() => {
  return data.value?.image_file
    ? createArticleImageUrl({ path: data.value.image_file })
    : undefined;
});

const { ogImage } = useOgImage(imageFile.value);

const description = computed(() => {
  return (
    data.value?.lead ||
    'ダイエットプラスの提案する「正しく食べてやせる」ダイエット。おしくてヘルシーなレシピ、ダイエットニュースなど。いままで何をしても痩せなかった人にオススメのダイエット情報を提供。'
  );
});

useSeoMeta({
  title,
  description,
  ogImage,
});

const currentPage = computed(() => {
  return normalizeNumberQueryParameter(route.params.page as string, 1);
});

async function changePage(page: number) {
  await navigateTo({
    name: 'public-article-news-permalink-page',
    params: {
      page,
      permalink: route.params.permalink,
    },
  });
  articleContentsRef.value?.scrollToContents();
}

const { paginatedContents } = useTransformArticle(data.value!);

if (currentPage.value < 1 || !paginatedContents.value[currentPage.value - 1]) {
  await navigateTo(
    {
      name: 'public-article-news-permalink',
      params: {
        permalink: route.params.permalink,
      },
    },
    { replace: true },
  );
}

async function scrollToSection(page: number, pageSection: number) {
  if (page !== currentPage.value) {
    await navigateTo({
      name: 'public-article-news-permalink-page',
      params: {
        permalink: route.params.permalink,
        page,
      },
    });
  }
  articleContentsRef.value?.contentAnchorRefs?.[
    pageSection - 1
  ]?.scrollIntoView({
    behavior: 'smooth',
  });
}

async function createPageView() {
  if (!data.value) {
    throw createError({
      statusCode: 404,
      message: 'pageviewの追加に失敗しました',
    });
  }
  await useFetch('/api/kiwi_user/articles/page_view/new', {
    method: 'POST',
    body: {
      article_id: data.value.id,
    },
    server: false,
  });
}

createPageView();
</script>

<template>
  <div>
    <div class="border-gray-300 border rounded mb-5 px-5 py-10">
      <ArticleContents
        v-if="data"
        ref="articleContentsRef"
        :current-page="currentPage"
        :article="data"
        :is-pr="isPrArticle(data)"
        :categories="categories"
        @change-page="changePage"
        @clicked-toc="scrollToSection($event.page, $event.pageSection)"
      >
        <template #share>
          <AtomsShareSection />
        </template>

        <template #footer>
          <OrganismsTaxelWidget />
        </template>
      </ArticleContents>
    </div>

    <OrganismsNewsesByCategory class="mb-5" />
  </div>
</template>
